import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import analytics from '../lib/tracking';

export default function FeedbackPage() {
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | 'general'>('general');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  // Use ref to track if form was submitted
  const showSuccessRef = useRef(false);

  // Update ref when submission state changes
  useEffect(() => {
    showSuccessRef.current = showSuccess;
  }, [showSuccess]);

  // Track page view on mount
  useEffect(() => {
    analytics.engagement('open_feedback_page', 'page', {});
  }, []);

  // Track form abandonment on unmount (unless user submitted)
  useEffect(() => {
    return () => {
      if (!showSuccessRef.current) {
        analytics.forms.general_feedback.abandon();
      }
    };
  }, []); // Empty deps - only runs on mount/unmount

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setFeedback(newValue);

    // Track when user starts typing (first keystroke)
    if (!hasStartedTyping && newValue.trim()) {
      setHasStartedTyping(true);
      analytics.forms.general_feedback.start();
    }
  };

  const handleSubmit = () => {
    if (!feedback.trim()) return;

    // Track form submission with type-safe API
    analytics.forms.general_feedback.submit({
      form_text: feedback,
      feedback_type: feedbackType,
      page_path: window.location.pathname,
    });

    // Show success message
    setShowSuccess(true);
    setFeedback('');
    setHasStartedTyping(false);

    // Hide success message and navigate back after 2 seconds
    setTimeout(() => {
      setShowSuccess(false);
      navigate('/');
    }, 2000);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-app-bg px-4 pb-24">
      {/* Header */}
      <div className="pt-6 pb-4 flex items-center">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 text-text-medium hover:text-text-dark transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="ml-2 text-2xl font-bold text-text-dark">Feedback</h1>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 rounded-xl text-green-800 text-center">
          Thank you! Your feedback has been received.
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-2xl mx-auto">
        {/* Description */}
        <p className="text-text-medium text-sm mb-6 leading-relaxed">
          We'd love to hear from you! Share your thoughts, report bugs, or suggest features to help us improve. Be honest, be brutal!
        </p>

        {/* Feedback Type Selector */}
        <div className="mb-4">
          <label className="block text-text-dark text-sm font-medium mb-2">
            What type of feedback is this?
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setFeedbackType('general')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                feedbackType === 'general'
                  ? 'bg-primary-pink text-white'
                  : 'bg-gray-100 text-text-medium hover:bg-gray-200'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setFeedbackType('feature')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                feedbackType === 'feature'
                  ? 'bg-primary-pink text-white'
                  : 'bg-gray-100 text-text-medium hover:bg-gray-200'
              }`}
            >
              Feature Request
            </button>
            <button
              onClick={() => setFeedbackType('bug')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                feedbackType === 'bug'
                  ? 'bg-primary-pink text-white'
                  : 'bg-gray-100 text-text-medium hover:bg-gray-200'
              }`}
            >
              Bug Report
            </button>
          </div>
        </div>

        {/* Feedback Textarea */}
        <div className="mb-6">
          <label className="block text-text-dark text-sm font-medium mb-2">
            Your Feedback
          </label>
          <textarea
            value={feedback}
            onChange={handleTextChange}
            placeholder="Share your thoughts, ideas, or report any issues you've encountered..."
            className="w-full h-48 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-pink resize-none text-text-dark"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!feedback.trim() || showSuccess}
          className="w-full bg-gradient-to-br from-primary-pink to-pastel-purple text-white font-medium py-4 px-8 rounded-xl transition-transform active:scale-95 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          Submit Feedback
        </button>
      </div>
    </div>
  );
}
